import {
  Button,
  CustomFilter,
  DatePicker,
  EllipsisSpan,
  Form,
  Paging,
  Radio,
  Select,
  Table,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
} from '@maxtropy/components';
import { useRequest } from 'ahooks';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { apiV2BillingMeterRechargePagePost } from '@maxtropy/device-customer-apis-v2';

interface MeterRechargeLogItem {
  id?: number;
  meterName?: string; // 计费电表名称
  money?: number; // 充值金额
  rechargeTime?: string; // 充值时间
  operatorName?: string; // 操作人
}

interface SearchParams {
  startTime?: string;
  endTime?: string;
}

const MeterRechargeLog = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const pagingInfo = usePaging(20);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'actual' | 'virtual'>('virtual');

  const defaultDateRange = useMemo(() => {
    const endTime = dayjs().endOf('day');
    const startTime = dayjs().subtract(30, 'day').startOf('day');
    return {
      startTime: startTime.format('YYYY-MM-DDTHH:mm:ss'),
      endTime: endTime.format('YYYY-MM-DDTHH:mm:ss'),
    };
  }, []);

  const { data: meterRechargeData, loading: tableLoading } = useRequest(
    () =>
      apiV2BillingMeterRechargePagePost({
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res?.total ?? 0);
        return res.list || [];
      }),
    {
      refreshDeps: [pageOffset, pageSize, searchParams],
      ready: !!searchParams,
    }
  );

  useEffect(() => {
    form.setFieldsValue({
      dateRange: [dayjs(defaultDateRange.startTime), dayjs(defaultDateRange.endTime)],
    });
    setSearchParams({
      startTime: defaultDateRange.startTime,
      endTime: defaultDateRange.endTime,
    });
  }, [form, defaultDateRange]);

  const dateRangeValidator = async (_: any, value: [Dayjs, Dayjs] | null) => {
    if (!value || !value[0] || !value[1]) {
      return Promise.resolve();
    }
    const [start, end] = value;
    if (end.isBefore(start)) {
      return Promise.reject(new Error('结束时间不能早于开始时间'));
    }
    const diffDays = end.diff(start, 'day');
    if (diffDays > 365) {
      return Promise.reject(new Error('选择的时间范围不能超过 365 天'));
    }
    return Promise.resolve();
  };

  const onReset = () => {
    setSearchParams({
      startTime: defaultDateRange.startTime,
      endTime: defaultDateRange.endTime,
    });
    setPageOffset(1);
    setTimeout(() => {
      form.setFieldsValue({
        dateRange: [dayjs(defaultDateRange.startTime), dayjs(defaultDateRange.endTime)],
      });
    }, 0);
  };

  const onFinish = (values: any) => {
    const dateRange = values.dateRange;
    if (!dateRange || dateRange.length !== 2) {
      return;
    }
    const startTime = dayjs(dateRange[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
    const endTime = dayjs(dateRange[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss');

    setSearchParams({
      startTime,
      endTime,
    });
  };

  // 表格排序变化处理
  const handleTableChange: TableProps<MeterRechargeLogItem>['onChange'] = (pagination, filters, sorter) => {
    if (sorter && !Array.isArray(sorter)) {
      setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  // 根据排序状态对数据进行排序
  const sortedData = useMemo(() => {
    const data = [...(meterRechargeData || [])];
    data.sort((a: MeterRechargeLogItem, b: MeterRechargeLogItem) => {
      const aVal = a.money ?? 0;
      const bVal = b.money ?? 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return data;
  }, [meterRechargeData, sortOrder]);

  const buildColumns = useMemo<ColumnsType<MeterRechargeLogItem>>(() => {
    return [
      {
        title: '计费电表名称',
        dataIndex: 'meterName',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v || '-'} />,
      },
      {
        title: activeTab === 'actual' ? '实际充值金额' : '虚拟充值金额',
        dataIndex: 'money',
        ellipsis: { showTitle: true },
        sorter: true,
        sortOrder: sortOrder === 'asc' ? 'ascend' : 'descend',
        render: (v: number) => <EllipsisSpan value={v !== null && v !== undefined ? `${v.toFixed(2)}元` : '-'} />,
      },
    ];
  }, [activeTab, sortOrder]);

  const filters = (
    <CustomFilter form={form} onFinish={onFinish} onReset={onReset}>
      <Form.Item name="dateRange" label="时间范围" rules={[{ validator: dateRangeValidator }]}>
        <DatePicker.RangePicker
          style={{ width: '100%' }}
          disabledDate={current => {
            return current && current.isAfter(dayjs().endOf('day'));
          }}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]} filters={filters}>
      <Radio.Group
        value={activeTab}
        onChange={e => {
          setActiveTab(e.target.value);
          setSortOrder('desc');
        }}
        optionType="button"
        buttonStyle="solid"
      >
        {/* <Radio.Button value="actual">实际充值金额</Radio.Button> */}
        <Radio.Button value="virtual">虚拟充值金额</Radio.Button>
      </Radio.Group>
      <Table
        rowKey="id"
        loading={tableLoading}
        dataSource={sortedData}
        columns={buildColumns}
        onChange={handleTableChange}
      />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};

export default MeterRechargeLog;
